import { Component, Prop, State, Event, EventEmitter, h, Host, Method, Watch } from '@stencil/core';
import { CountriesList } from '../../assets/countries';

@Component({
  tag: 'dda-phonefield',
  styleUrls: [],
  shadow: false,
})
export class DdaPhoneField {
  @Prop() label: string;
  @Prop() placeholder: string = 'Enter phone number';
  @Prop() helper_text: string;
  @Prop() max_length: string;
  @Prop() validation_type?: string;
  @Prop() error_message: string;
  @Prop() validation_id: string = 'text';
  @Prop() disabled: boolean = false;
  @Prop() size?: string;
  @Prop() custom_class: string;
  @Prop() component_mode?: string;
  @Prop() input_id?: string;
  @Prop() aria_label: string;
  @Prop() button_id?: string;
  @Prop() button_aria_label: string;
  @Prop() toggle_button_name: string;
  @Prop() country_select_button_name: string;
  @Prop() phone_input_name: string;
  @Prop() value: string;

  @State() country_code: string = '+971';
  @State() country_flag: string = 'https://flagcdn.com/w320/ae.png';
  @State() internalValue: string = '';
  @State() is_focused: boolean = false;
  @State() dropdown_open: boolean = false;
  @State() countries: { code: string; flag: string }[] = [];
  @State() hostElement: HTMLElement;

  @Event() phoneBlurred: EventEmitter<{ name: string; value: string }>;
  @Event() onBlurred: EventEmitter<any>;

  @Method()
  async getValue() {
    return this.internalValue || this.value || '';
  }

  @Method()
  async validatePhoneField() {
    this.phoneBlurred.emit({
      name: this.phone_input_name,
      value: this.internalValue.trim(),
    });
    this.onBlurred.emit({
      name: this.phone_input_name,
      value: this.internalValue.trim(),
    });
  }

  @Watch('value')
  watchHandler(newValue: string) {
    this.internalValue = newValue;
  }

  componentWillLoad() {
    this.countries = CountriesList;
    this.internalValue = this.value || '';
  }

  componentDidLoad() {
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  handleDocumentClick = (event: MouseEvent) => {
    if (this.dropdown_open && this.hostElement && !this.hostElement.contains(event.target as Node)) {
      this.dropdown_open = false;
    }
  };

  toggleDropdown() {
    this.dropdown_open = !this.dropdown_open;
  }

  selectCountry(country: { code: string; flag: string }) {
    this.country_code = country.code;
    this.country_flag = country.flag;
    this.dropdown_open = false;
  }

  handleFocus() {
    this.is_focused = true;
  }

  handleBlur() {
    this.is_focused = false;
    this.phoneBlurred.emit({
      name: this.phone_input_name,
      value: this.internalValue.trim(),
    });
    this.onBlurred.emit(this.internalValue.trim());
  }

  handlephonenumberChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const targetvalue = target.value.replace(/\D/g, '');
    this.internalValue = targetvalue;
  }

  handleKeyDown(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];

    if (
      allowedKeys.includes(event.key) ||
      event.ctrlKey ||
      event.metaKey // Allow copy/paste shortcuts
    ) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  render() {
    const inputClass = `${this.component_mode} ${this.size ? `dda-input-size-${this.size}` : ''} ${this.error_message ? `dda-error-message` : ''} ${this.validation_type ? `dda-validation-${this.validation_type}` : ''} ${this.is_focused ? 'dda-input-focus' : ''} ${this.internalValue ? 'dda-input-focus-filled' : ''}`;

    return (
      <Host ref={el => (this.hostElement = el as HTMLElement)}>
        <div class={`dda-input-container ${inputClass} ${this.custom_class} ${this.disabled ? 'dda-input-disabled' : ''}`}>
          {this.label && (
            <label htmlFor={this.input_id} class="dda-input-label">
              {this.label}
            </label>
          )}
          <div class={`dda-input-field-group dda-phone-field`}>
            <div class="dda-input-dropdown-btn">
              <button type="button" name={this.toggle_button_name} class="dda-dropdown-select" onClick={() => this.toggleDropdown()}>
                <img src={this.country_flag} alt="" width="20" /> {this.country_code}
                <i class="material-icons" aria-hidden="true">
                  keyboard_arrow_down
                </i>
              </button>
              {this.dropdown_open && (
                <div class="dda-input-dropdown-list">
                  {this.countries.map(country => (
                    <button
                      id={`countryCode${country.code}`}
                      name={this.country_select_button_name}
                      aria-label={this.button_aria_label}
                      type="button"
                      class="country-select dda-input-dropdown-item"
                      onClick={() => this.selectCountry(country)}
                    >
                      <img src={country.flag} alt="" width="20" /> {country.code}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              id={this.input_id}
              name={this.phone_input_name}
              aria-label={this.aria_label}
              type="text"
              inputmode="numeric"
              maxlength={this.max_length}
              pattern="[0-9]*"
              class="dda-field-group-input"
              placeholder={this.placeholder}
              value={this.internalValue}
              disabled={this.disabled}
              onFocus={this.handleFocus.bind(this)}
              onInput={e => this.handlephonenumberChange(e)}
              onKeyDown={e => this.handleKeyDown(e)}
              onBlur={() => this.handleBlur()}
            />
          </div>

          {this.validation_type === 'error' && this.error_message ? (
            <span class="dda-error-message" id={this.validation_id} role="alert">
              {this.error_message}
            </span>
          ) : (
            this.helper_text && (
              <span class="dda-helper-text" id={this.validation_id}>
                {this.helper_text}
              </span>
            )
          )}
        </div>
      </Host>
    );
  }
}
